/**
 * What a smaller copy of a video is aimed at, and how big it is expected to
 * come out (D-903, D-912).
 *
 * **Its own module because two parts of one feature need the same answer and
 * must not each have a guess at it.** The encoder aims at these numbers; the
 * screen quotes a wait that depends on them. They disagreed until 2026-10-19,
 * and the way it showed up is the one that costs trust: the quoted wait for a
 * smaller copy was **half** the truth on a whole class of real clips.
 *
 * ⚠ **It must never import `mediabunny`.** `videoCompress.ts` is deliberately a
 * lazily-loaded 539 KB chunk, fetched only when she accepts the offer — and the
 * forecast runs on every tile, in the main bundle. A shared module that reached
 * for the library would drag all of it back in.
 */

/**
 * The target, as one tunable pair.
 *
 * `asked`: *"roughly 1080p30 at a deliberately high bitrate for motion"*,
 * because **dance is the hard case** — fast limb movement is exactly what a
 * low bitrate smears, and footwork and timing are the whole reason a teacher
 * is watching.
 *
 * ⚠ **Never ask for more than the source already had.** The first run of the
 * prototype requested a flat 5 Mbps against a clip that was already 4.1, and
 * produced a file *half again as large* — twice, before anybody noticed. The
 * share below is what stops that, and the never-worse rule in `videoCompress`
 * is what would have caught it anyway.
 *
 * **This pair is the quality dial.** Raise either and the picture improves, the
 * file grows, and — because the forecast below reads the same numbers — the
 * wait she is quoted follows without anybody remembering to change it.
 * `observed` 2026-10-19 on a real 1:46 dance clip at 4.09 Mbps: SSIM **0.987**
 * on the busiest moments, which the lab's own scale calls indistinguishable.
 */
export const TARGET_BPS = 5_000_000
export const MOST_OF_SOURCE = 0.6

/**
 * The cap is on the **short** edge, in **display** space.
 *
 * ⚠ **Both halves of that were wrong first, and the same input broke both.**
 * An iPhone held upright records **coded 1920×1080 with a 90° rotation
 * matrix** — its display size is 1080×1920. Reading `codedHeight` gave 1080
 * and concluded "already 1080p, nothing to scale", while the library resolves
 * `height` against the **display** size — so it produced **608×1080**. A
 * quarter of the pixels, unasked, on the likeliest input in the feature, with
 * the bitrate chosen as if for a full-resolution encode.
 *
 * And capping *height* rather than the short edge would have done it again by
 * a different route: for an upright clip the long edge is the height, so
 * "1080" would mean 608×1080 every time. **Short-edge 1080 is what "1080p"
 * means to a person** — 1920×1080 landscape and 1080×1920 portrait are the
 * same quality — and it is what the field-reported settings for this library
 * use.
 *
 * **It does not enter the size forecast**, deliberately: the encoder aims at a
 * *bitrate*, so scaling a 4K source down changes how good those bits look and
 * not how many of them there are.
 */
export const MAX_SHORT_EDGE = 1080

/**
 * Below this, leave it alone.
 *
 * ⚠ **Keyed on bitrate, not on file size.** The spec said size, and the
 * prototype showed why that is the wrong axis: a 51.8 MB clip already at
 * 4.1 Mbps has almost nothing to give, while a 40 MB clip at 20 Mbps has a
 * great deal. Size alone cannot tell those apart.
 */
export const ALREADY_LEAN_BPS = 2_500_000

/**
 * What the audio is assumed to add, since it is **copied and never
 * re-encoded** (D-903).
 *
 * ⚠ **The one term that has to be assumed, and leaving it out biased the
 * prediction the dangerous way.** The target below is a *video* bitrate, so the
 * copied audio track rides on top of it — and the whole residual of the first
 * real measurement was exactly that: **208,095 bps** on a clip predicted 7.8%
 * short, which is a textbook stereo AAC rate. Short means the quoted wait is
 * too good, and the error grows as the target falls: just above the leave-alone
 * floor the same track is about **14%** of the answer.
 *
 * A nominal figure rather than a read one, because knowing it means opening the
 * file with the demuxer — which is `mediabunny`, which this module must not
 * load. **A clip with no audio is over-predicted by this much**, which quotes a
 * slightly longer wait than she gets, and that is the direction to be wrong in.
 */
const ASSUMED_AUDIO_BPS = 192_000

/**
 * The shortest clip worth forecasting.
 *
 * ⚠ **Because the predicted size now depends on the running time, a wrong
 * length is a wrong offer** — and `onDuration` admits anything finite and
 * positive, which a fragmented or stream-written MOV can get wrong. `observed`:
 * a 60 MB file reporting **0.04 seconds** forecast a smaller copy at *a tenth
 * of a second* against a four-minute upload, offered it, and handed the encoder
 * a 245-second deadline that could never fire. Over-reporting is harmless (the
 * source bitrate collapses under the floor and nothing is offered); it is the
 * absurdly short end that needed a floor, and no real video is under a second.
 */
const SHORTEST_WORTH_FORECASTING = 1

/** What the source was running at, in bits a second. */
export function sourceBitrate(bytes: number, durationSeconds: number): number {
  if (!(bytes > 0) || !(durationSeconds > 0)) return 0

  return (bytes * 8) / durationSeconds
}

/**
 * The video bitrate a smaller copy is encoded at.
 *
 * **Shared so the encoder and the forecast cannot hold different formulas** —
 * moving the constants alone left this `Math.min` written out in both places,
 * which is the drift A-17 is about and exactly how the two came to disagree.
 * It takes an already-computed source rate rather than the file, because the
 * encoder asks the demuxer for a duration this module must not load.
 */
export function targetBitrate(sourceBps: number): number {
  return Math.min(TARGET_BPS, sourceBps * MOST_OF_SOURCE)
}

/**
 * How big a smaller copy is expected to be, or null when that cannot be known.
 *
 * ⚠ **Computed, where it used to be a flat "a third of the original".** That
 * guess was wrong by **1.95x** on the first real clip it was measured against —
 * 35,352,844 bytes where it predicted 18,105,770 — and wrong in the direction that makes the
 * quoted wait too short, which this feature's own rule calls the one that stops
 * somebody trusting the number. Its comment even called a third *"the
 * conservative end of what was measured"*, while the measured range on a lean
 * clip was 1.3x to 2.6x; a third means **3x**, past the far end of it.
 *
 * The encoder aims at a bitrate, so the size follows from the bitrate and the
 * running time, and only the copied audio has to be assumed. `observed` against
 * a real encode of 35,352,844 bytes: **35,139,185 predicted, 0.6% under**,
 * where the flat third said 18,105,770 — 48.8% under.
 *
 * **Null means do not offer**, for three reasons: no usable running time, a
 * clip so short the length cannot be believed, and a clip already under the
 * leave-alone floor — which the encoder would hand straight back, so an offer
 * there is a saving of nothing dressed up as a choice.
 */
export function expectedBytesAfter(bytes: number, durationSeconds: number): number | null {
  if (!(durationSeconds >= SHORTEST_WORTH_FORECASTING)) return null

  const source = sourceBitrate(bytes, durationSeconds)

  if (source < ALREADY_LEAN_BPS) return null

  return ((targetBitrate(source) + ASSUMED_AUDIO_BPS) * durationSeconds) / 8
}
